<template>
  <div class="form-option-page">
    <div class="fo-top">
      <div class="fo-top-l">
        <div class="fo-title">
          <el-button icon="ArrowLeft" text @click="goList">返回</el-button>
          <h2>表单数据源</h2>
          <span class="fo-badge">{{ pageTotal }} 项</span>
        </div>
        <p class="fo-sub">
          按城市维护「测试项目」等选项，供填写端级联选择；也可用「分组」扩展自定义选项集。
        </p>
      </div>
      <div class="fo-top-r">
        <el-button icon="Refresh" :disabled="saving" @click="selectList">刷 新</el-button>
        <el-button icon="Sort" :loading="saving" :disabled="saving" @click="saveSort">保存排序</el-button>
        <el-button type="primary" icon="Plus" @click="openBatch">批量新增</el-button>
      </div>
    </div>

    <!-- ★ 学校已改为填写端手写输入，此处的学校分组仅供历史表单兼容 -->
    <el-alert
        v-if="query.groupKey === 'school'"
        type="info"
        show-icon
        :closable="false"
        style="margin-bottom: 12px"
        title="学校已改为「填写端手写输入」，此处无需再维护"
        description="设计器中的「学校名称」字段是手写文本框，家长直接输入学校名，后台按名称模糊筛选与统计。这里的学校分组仅用于兼容历史表单（早期按选项选择）。新表单请直接用「学校名称」字段。"></el-alert>

    <!-- 筛选 -->
    <div class="fo-bar">
      <el-select
          v-model="query.groupKey"
          filterable
          allow-create
          default-first-option
          placeholder="选择/输入分组"
          style="width: 180px;margin-right: 10px">
        <el-option
            v-for="g in groupKeyOptions"
            :key="g.value"
            :label="g.label"
            :value="g.value"></el-option>
      </el-select>
      <el-select
          v-model="query.cityId"
          clearable
          filterable
          placeholder="全部城市"
          style="width: 180px;margin-right: 10px">
        <el-option v-for="c in cityOptions" :key="c.id" :label="c.name" :value="c.id"></el-option>
      </el-select>
      <el-input
          v-model="query.keyword"
          clearable
          placeholder="关键字"
          style="width: 160px;margin-right: 10px"
          @keyup.enter="search"></el-input>
      <el-button type="primary" icon="Search" @click="search">查 询</el-button>
      <el-button icon="RefreshLeft" @click="reset">重 置</el-button>
      <span class="fo-tip">
        <el-icon><InfoFilled /></el-icon>
        改完排序记得点右上角「保存排序」才会生效
      </span>
    </div>

    <!-- 列表 -->
    <div class="fo-card">
    <el-table
        v-loading="listLoading"
        :data="tableData"
        border>
      <el-table-column width="70" align="center" label="排序">
        <template #default="scope">
          <el-input-number
              v-model="scope.row.sort"
              :min="0"
              size="small"
              controls-position="right"
              style="width: 66px"></el-input-number>
        </template>
      </el-table-column>
      <el-table-column min-width="180" show-overflow-tooltip prop="label" label="选项名称"/>
      <el-table-column min-width="140" show-overflow-tooltip prop="value" label="提交值"/>
      <el-table-column width="120" align="center" label="城市">
        <template #default="scope">{{ cityName(scope.row.cityId) }}</template>
      </el-table-column>
      <el-table-column width="120" align="center" label="分组">
        <template #default="scope">
          <el-tag size="small" effect="plain">{{ groupName(scope.row.groupKey) }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column width="170" align="center" fixed="right" label="操作">
        <template #default="scope">
          <el-button size="small" text type="primary" @click="openEdit(scope.row)">编辑</el-button>
          <el-button size="small" text type="primary" :disabled="scope.$index === 0" @click="moveUp(scope.$index)">上移</el-button>
          <el-button size="small" text type="primary" :disabled="scope.$index === tableData.length - 1" @click="moveDown(scope.$index)">下移</el-button>
          <el-button size="small" text type="danger" @click="deleteItem(scope.row)">删除</el-button>
        </template>
      </el-table-column>

      <!-- 空态出口：直接给「批量新增」按钮，不留死页（有筛选时再给「重置筛选」） -->
      <template #empty>
        <div class="fo-empty">
          <el-icon><Coin /></el-icon>
          <p>{{ query.keyword || query.cityId ? '当前筛选下没有数据源' : '这个分组下还没有数据源' }}</p>
          <el-button type="primary" icon="Plus" @click="openBatch">批量新增</el-button>
          <el-button v-if="query.keyword || query.cityId" text type="primary" @click="reset">重置筛选</el-button>
        </div>
      </template>
    </el-table>

    <div class="pagination">
      <el-pagination
          background
          layout="total, prev, pager, next"
          :current-page="query.pageIndex"
          :page-size="query.pageSize"
          :total="pageTotal"
          @current-change="handlePageChange"></el-pagination>
    </div>
    </div>

    <!-- 批量新增 -->
    <el-dialog v-model="batchVisible" title="批量新增选项" width="480px">
      <el-form label-width="80px">
        <el-form-item label="分组">
          <el-select v-model="batchForm.groupKey" filterable allow-create default-first-option style="width: 100%">
            <el-option v-for="g in groupKeyOptions" :key="g.value" :label="g.label" :value="g.value"></el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="城市">
          <el-select v-model="batchForm.cityId" clearable filterable style="width: 100%">
            <el-option v-for="c in cityOptions" :key="c.id" :label="c.name" :value="c.id"></el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="选项">
          <el-input
              type="textarea"
              :rows="8"
              v-model="batchForm.text"
              placeholder="一行一个选项，例如：&#10;花园路小学&#10;文化路小学"></el-input>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="batchVisible = false">取 消</el-button>
        <el-button type="primary" :loading="batchSaving" @click="submitBatch">确 定</el-button>
      </template>
    </el-dialog>

    <!-- 编辑 -->
    <el-dialog v-model="editVisible" title="编辑选项" width="420px">
      <el-form label-width="80px">
        <el-form-item label="选项名称">
          <el-input v-model="editForm.label"></el-input>
        </el-form-item>
        <el-form-item label="提交值">
          <el-input v-model="editForm.value"></el-input>
        </el-form-item>
        <el-form-item label="排序">
          <el-input-number v-model="editForm.sort" :min="0" controls-position="right"></el-input-number>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="editVisible = false">取 消</el-button>
        <el-button type="primary" :loading="editSaving" @click="submitEdit">确 定</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import {
  formOptionList,
  formOptionSave,
  formOptionBatch,
  formOptionDelete,
  GROUP_KEY_OPTIONS,
  formCities,
} from '@/api/form';

export default {
  name: 'FormOption',
  data() {
    return {
      listLoading: false,
      saving: false,
      tableData: [],
      pageTotal: 0,
      cityOptions: [],
      groupKeyOptions: GROUP_KEY_OPTIONS,
      query: {
        // 学校已改手写 → 默认落在「测试项目」
        groupKey: 'project',
        cityId: null,
        keyword: '',
        pageIndex: 1,
        pageSize: 20,
      },
      batchVisible: false,
      batchSaving: false,
      batchForm: { groupKey: 'project', cityId: null, text: '' },
      editVisible: false,
      editSaving: false,
      editForm: { id: null, label: '', value: '', sort: 0 },
    };
  },
  methods: {
    cityName(cityId) {
      if (cityId == null) return '全部';
      const c = this.cityOptions.find(x => x.id === cityId);
      return c ? c.name : cityId;
    },
    groupName(key) {
      const g = this.groupKeyOptions.find(x => x.value === key);
      return g ? g.label : key;
    },
    /** 回表单列表（数据源是「表单管理」的配套页，不该变成孤岛） */
    goList() {
      const p = this.$router.push({ path: '/formList' });
      if (p && typeof p.catch === 'function') {
        p.catch(err => this.$message.error(`返回失败：${(err && err.message) || '未知错误'}`));
      }
    },
    search() {
      this.query.pageIndex = 1;
      this.selectList();
    },
    reset() {
      this.query.groupKey = 'project';
      this.query.cityId = null;
      this.query.keyword = '';
      this.query.pageIndex = 1;
      this.selectList();
    },
    handlePageChange(index) {
      this.query.pageIndex = index;
      this.selectList();
    },
    selectList() {
      this.listLoading = true;
      formOptionList(this.query).then(val => {
        this.listLoading = false;
        if (val && val.code === 200) {
          const data = val.data || {};
          this.tableData = Array.isArray(data) ? data : (data.records || []);
          this.pageTotal = Array.isArray(data) ? data.length : (data.total || 0);
        } else {
          this.$message.warning((val && val.message) || '获取数据源失败');
        }
      }).catch(() => {
        this.listLoading = false;
      });
    },
    openBatch() {
      this.batchForm = {
        groupKey: this.query.groupKey || 'project',
        cityId: this.query.cityId,
        text: '',
      };
      this.batchVisible = true;
    },
    submitBatch() {
      const lines = (this.batchForm.text || '')
          .split('\n')
          .map(s => s.trim())
          .filter(s => s !== '');
      if (!lines.length) {
        this.$message.warning('请至少输入一行选项');
        return;
      }
      if (!this.batchForm.groupKey) {
        this.$message.warning('请选择分组');
        return;
      }
      this.batchSaving = true;
      formOptionBatch({
        groupKey: this.batchForm.groupKey,
        cityId: this.batchForm.cityId,
        labels: lines,
      }).then(val => {
        this.batchSaving = false;
        if (val && val.code === 200) {
          this.$message.success(`已新增 ${(val.data != null ? val.data : lines.length)} 项`);
          this.batchVisible = false;
          this.query.groupKey = this.batchForm.groupKey;
          this.query.cityId = this.batchForm.cityId;
          this.selectList();
        } else {
          this.$message.warning((val && val.message) || '新增失败');
        }
      }).catch(() => {
        this.batchSaving = false;
      });
    },
    openEdit(row) {
      this.editForm = {
        id: row.id,
        label: row.label,
        value: row.value,
        sort: row.sort == null ? 0 : row.sort,
        cityId: row.cityId,
        groupKey: row.groupKey,
      };
      this.editVisible = true;
    },
    submitEdit() {
      if (!this.editForm.label) {
        this.$message.warning('请填写选项名称');
        return;
      }
      this.editSaving = true;
      const payload = {
        id: this.editForm.id,
        label: this.editForm.label,
        value: this.editForm.value || this.editForm.label,
        sort: this.editForm.sort,
        cityId: this.editForm.cityId,
        groupKey: this.editForm.groupKey,
      };
      formOptionSave(payload).then(val => {
        this.editSaving = false;
        if (val && val.code === 200) {
          this.$message.success('保存成功');
          this.editVisible = false;
          this.selectList();
        } else {
          this.$message.warning((val && val.message) || '保存失败');
        }
      }).catch(() => {
        this.editSaving = false;
      });
    },
    deleteItem(row) {
      this.$confirm(`确定删除选项「${row.label}」吗？`, '提示', { type: 'warning' }).then(() => {
        formOptionDelete(row.id).then(val => {
          if (val && val.code === 200) {
            this.$message.success('删除成功');
            this.selectList();
          } else {
            this.$message.warning((val && val.message) || '删除失败');
          }
        });
      }).catch(() => {});
    },
    moveUp(idx) {
      this.swapSort(idx, idx - 1);
    },
    moveDown(idx) {
      this.swapSort(idx, idx + 1);
    },
    swapSort(i, j) {
      // 越界 / 原地保护：上移第一行、下移最后一行均为安全 no-op
      if (i < 0 || j < 0 || i >= this.tableData.length || j >= this.tableData.length || i === j) return;
      // 交换数组位置后整体重排 1..n：避免后端 sort 全为 0 时「只换值」等于没换
      const arr = this.tableData.slice();
      const tmp = arr[i];
      arr[i] = arr[j];
      arr[j] = tmp;
      arr.forEach((r, idx) => { r.sort = idx + 1; });
      this.saving = true;
      Promise.all(arr.map(r => formOptionSave(this.toSavePayload(r)))).then(() => {
        this.saving = false;
        this.selectList();
      }).catch(() => {
        this.saving = false;
        this.$message.warning('排序保存失败');
      });
    },
    toSavePayload(r) {
      return {
        id: r.id,
        sort: r.sort,
        cityId: r.cityId,
        groupKey: r.groupKey,
        label: r.label,
        value: r.value,
      };
    },
    saveSort() {
      if (!this.tableData.length) return;
      this.tableData.forEach((r, i) => { r.sort = i + 1; });
      this.saving = true;
      Promise.all(this.tableData.map(r => formOptionSave(this.toSavePayload(r)))).then(() => {
        this.saving = false;
        this.$message.success('排序已保存');
        this.selectList();
      }).catch(() => {
        this.saving = false;
        this.$message.warning('排序保存失败');
      });
    },
    loadCityOptions() {
      formCities().then(val => {
        const list = (val && val.code === 200 && Array.isArray(val.data))
            ? val.data.filter(n => n && n.id != null).map(n => ({ id: n.id, name: n.name }))
            : [];
        // cityId=0 表示「通用（所有城市可见）」，后端约定
        this.cityOptions = [{ id: 0, name: '通用（所有城市）' }].concat(list);
      });
    },
  },
  created() {
    this.loadCityOptions();
    this.selectList();
  },
};
</script>

<style scoped>

/* @form-tokens:start */
/* ======================= 表单模块设计令牌（v3.11 视觉统一）=======================
 * ★ 本块在 9 个表单页各存一份（CSS 变量按作用域隔离，无法跨 scoped 共享）。
 *   改配色的唯一入口 = D:/tmp/shot/form_tokens.py 的 TOKEN_BLOCK，改完重跑该脚本
 *   一键同步；**不要手改单页**，否则 9 份会漂移。
 * ★ 为什么不放全局 :root：--el-color-primary 一旦全局生效会波及后台所有模块
 *   （订单 / 教练 / 财务…）。本模块只对「表单 9 页」负责，故挂在页面根容器上，
 *   作用域精确、零外溢。
 * ========================================================================== */
.form-option-page {
  /* —— Element 主色对齐「焱火绿」——
     此前后台从未覆盖 --el-color-primary，Element 一直是默认**蓝色**，
     而页面自定义元素用 #8dc63f ⇒ 同屏「按钮蓝 + 品牌绿」两个颜色。
     这 7 个变量一改，本页所有 el-button / el-switch / el-radio / el-checkbox /
     el-slider / el-pagination / focus 描边… 一次性全部变绿。
     light-3/5/7/8/9 与 dark-2 必须一并给出，否则 hover / disabled 态会
     回退到 Element 基于**蓝色**算出的浅色，出现「绿底 + 蓝边」的跳变。 */
  --el-color-primary: #8dc63f;
  --el-color-primary-light-3: #afd779;
  --el-color-primary-light-5: #c6e39f;
  --el-color-primary-light-7: #ddeec5;
  --el-color-primary-light-8: #e8f4d9;
  --el-color-primary-light-9: #f4f9ec;
  --el-color-primary-dark-2: #719e32;

  /* —— 文字五档（严格对齐 Element 灰阶；置成令牌后同类文字不会再各写一个灰） —— */
  --yh-t1: #303133; /* 主文字 */
  --yh-t2: #606266; /* 常规 */
  --yh-t3: #909399; /* 次要 */
  --yh-t4: #a8abb2; /* 占位 */
  --yh-t5: #c0c4cc; /* 禁用 */

  /* —— 主色四档 —— */
  --yh-theme: #8dc63f;
  --yh-theme-d: #7cad37;
  --yh-theme-dd: #5f9427;
  --yh-theme-l: #eef7e2;
  --yh-theme-ll: #f6fbef;

  /* —— 面与线 —— */
  --yh-bg: #f5f7fa;
  --yh-fill: #fafbfc;
  --yh-line: #ebeef5;
  --yh-line-d: #dcdfe6;
  --yh-divide: #f2f3f5;

  /* —— 圆角五档 —— */
  --yh-r-xs: 4px;
  --yh-r-sm: 6px;
  --yh-r: 8px;
  --yh-r-lg: 12px;
  --yh-r-pill: 999px;
}
/* @form-tokens:end */
.form-option-page {
  padding: 16px 20px 30px;
  min-height: 100%;
  background-color: #f5f7fa;
  box-sizing: border-box;
}

/* ==================== 顶部 ==================== */
.fo-top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 20px;
  flex-wrap: wrap;
  background: #fff;
  border-radius: var(--yh-r-lg);
  padding: 14px 20px 16px;
  margin-bottom: 14px;
  box-shadow: 0 1px 4px rgba(0, 0, 21, 0.06);
}

.fo-title {
  display: flex;
  align-items: center;
  gap: 6px;
}

.fo-title h2 {
  margin: 0;
  font-size: 19px;
  font-weight: 600;
  color: var(--yh-t1);
}

.fo-badge {
  padding: 1px 9px;
  border-radius: var(--yh-r-pill);
  background: var(--yh-theme-l);
  color: var(--yh-theme-dd);
  font-size: 12px;
}

.fo-sub {
  margin: 6px 0 0;
  max-width: 720px;
  font-size: 12px;
  line-height: 1.8;
  color: var(--yh-t3);
}

.fo-top-r {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  flex: 0 0 auto;
}

/* ==================== 筛选 ==================== */
.fo-bar {
  background: #fff;
  padding: 12px 16px;
  margin-bottom: 12px;
  border-radius: var(--yh-r-lg);
  box-shadow: 0 1px 4px rgba(0, 0, 21, 0.06);
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
}

.fo-tip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-left: auto;
  font-size: 12px;
  color: var(--yh-t4);
}

/* ==================== 表格卡 ==================== */
.fo-card {
  background: #fff;
  border-radius: var(--yh-r-lg);
  padding: 6px 12px 14px;
  box-shadow: 0 1px 4px rgba(0, 0, 21, 0.06);
}

.fo-card :deep(.el-table__inner-wrapper::before) {
  display: none;
}

.fo-card :deep(.el-table__header th) {
  background: var(--yh-fill);
  color: var(--yh-t2);
  font-weight: 600;
}

.fo-card :deep(.el-table__row:hover > td) {
  background: var(--yh-fill) !important;
}

.pagination {
  margin-top: 16px;
  text-align: right;
}

/* 表格空态：图标 + 文案 + 出口按钮 */
.fo-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 24px 0 10px;
}

.fo-empty .el-icon {
  font-size: 30px;
  color: #dcdfe6;
}

.fo-empty p {
  margin: 0;
  font-size: 13px;
  color: var(--yh-t4);
}
</style>
